<template>
    <component 
        :is="componentTag" 
        :href="href" 
        :target="href ? '_blank' : false" 
        class="list-info-item" 
        :class="[
            {'list-info-item--link': isLink}, 
            {'list-info-item--icon': hasIcon}, 
            {'list-info-item--center-vertically': centerVertically}, 
            {'sidebar__section': sidebarSection}, 
            {'list-info-item--hide-summary-text': collapseHideSummaryText}, 
            {'list-info-item--no-border': noBorder},
            `list-info-item--${type}`
        ]"
        :name="collapseName ? collapseName : undefined">
        <div slot="title" class="list-info-item__summary" :class="arrowTopAligned ? 'ui-spread' : 'ui-spread-center-vertically'" @click="collapseClickHandler">
            <div class="list-info-item__summary__content print-break-avoid" :class="{'ui-overflow-gradient': overflowGradient}">
                <slot name="summary" />
            </div>
            <!-- <SmrTooltip :content="tooltips && !arrowTopAligned ? tooltips.content : ''" :position="tooltips ? tooltips.position : ''"> -->
            <SmrTooltip :content="tooltips ? tooltips.content : ''" :position="tooltips ? tooltips.position : ''">
                <icon class="list-info-item__summary__arrow" :class="{'list-info-item__summary__arrow--top': arrowTopAligned}" :width="20" :height="20" v-if="isLink && !loading"><IconChevronDown /></icon>
            </SmrTooltip>
            <Loader v-if="isLink && loading"/>
        </div>
        <div class="list-info-item__content" :class="{ 'list-info-item__content--space-top' : contentSpaceTop,  'list-info-item__content--full-width' : contentFullWidth }" v-if="hasContent || type === 'collapse'">
            <slot name="content" />
        </div>
    </component>
</template>

<script>
import Loader from '@/components/Loader.vue'
import Icon from '@/components/Icon.vue'
import IconChevronDown from '@/components/icons/IconChevronDown.vue'
import SmrTooltip from '@/components/SmrTooltip.vue'
import eventBus from '@/utils/event-bus';

export default {
    components: {
        Loader,
        Icon,
        IconChevronDown,
        SmrTooltip,
    },
    props: {
        type: {
            type: String,
            default: 'static'
        },
        href: {
            type: [String, Boolean],
            default: false
        },
        clickable: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        hasContent: {
            type: Boolean,
            default: false
        },
        hasIcon: {
            type: Boolean,
            default: true
        },
        sidebarSection: {
            type: Boolean,
            default: true
        },
        collapseHideSummaryText: {
            type: Boolean,
            default: false
        },
        overflowGradient: {
            type: Boolean,
            default: true
        },
        centerVertically: {
            type: Boolean,
            default: false
        },
        arrowTopAligned: {
            type: Boolean,
            default: false
        },
        contentSpaceTop: {
            type: Boolean,
            default: false
        },
        contentFullWidth: {
            type: Boolean,
            default: false
        },
        collapseName: {
            type: [Boolean, String],
            default: false
        },
        noBorder: {
            type: Boolean,
            default: false
        },
        tooltips: {
            type: [Boolean, Object],
            default: false,
        }
    },
    data() {
        return {
            arrowUp: false,
            content: this.tooltips.content ? this.tooltips.content : '',
        }
    },
    computed: {
        componentTag() {
            let tag = 'div';
            if(this.type === 'collapse') {
                tag = 'el-collapse-item';
            } else if (this.href) {
                tag = 'a';
            }
            return tag;
        },
        isLink() {
            return this.href || this.clickable || this.type === 'collapse';
        },
    },
    methods: {
        collapseClickHandler() {
            eventBus.$emit('triggerPOIDetailScrollbar');
            if(this.arrowUp) this.arrowUp = false;
            else this.arrowUp = true;
            
            if(this.tooltips.content && this.tooltips.closed && this.arrowUp === true) {
                this.tooltips.content = this.tooltips.closed;
            } else if(this.tooltips.content && this.tooltips.closed && this.arrowUp === false) {
               this.tooltips.content = this.content
            }
        }
    }
}
</script>

<style lang="scss">
.list-info-item {
    $block: &;
    position: relative;
    padding-top: 12px;
    padding-bottom: 12px;
    display: block;
    color: $color-black;
    text-decoration: none;
    &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 1px;
        background-color: $color-black-005;
    }
    &--link {
        #{$block}__summary {
            cursor: pointer;
        }
        .no-touch &:hover {
            #{$block}__summary__arrow {
                color: $color-black;
            }
        }
    }
    &--simple {
        &::after {
            content: none !important; 
        }
        #{$block} {
            &__summary {
                padding-left: $sidebar-horizontal-padding;
                padding-right: $sidebar-horizontal-padding;
            }
            &__content {
                padding-top: 20px;
                padding-left: $sidebar-horizontal-padding;
                padding-right: $sidebar-horizontal-padding;
            } 
        }
    }
    &--icon {
        &::after {
            left: 54px;
        }
        #{$block} {
            &__content {
                padding-left: 42px;
            }
        }
    }
    &--center-vertically {
        #{$block} {
            &__summary {
                &__content {
                    align-items: center;
                }
                &__icon {
                    margin-top: 0;
                }
            }
        }
    }

    &--no-border {
        &:after {
            content: none;
        }
    }

    &:not(.sidebar__section) {
        &:first-child {
            padding-top: 0;
        }
        &::after {
            right: -16px;
        }
    }

    &__summary {
        width: 100%;
        position: relative;
        &__content {
            display: flex;
            align-items: flex-start;
            line-height: 1.5;
            color: $color-black;
            flex-grow: 1;
        }
        &__image {
            width: 24px;
            margin-right: 18px;
        }
        &__icon {
            width: 24px;
            margin-right: 16px;
            color: $color-grey-dark;
            flex-shrink: 0;
            margin-top: 8px;
            &--small-margin {
                margin-right: 8px;
            }
            &--tiny-margin {
                margin-right: 2px;
            }
            &--no-top-margin {
                margin-top: 0;
            }
        }
        &__tag {
            margin-right: 8px;

            &--right {
                margin-right: 0;
                margin-left: 8px;
                line-height: 1.2;
                white-space: nowrap;
            }
        }
        &__infolabel {
            margin-right: 17px;
        }
        &__infoimage {
            width: 53px;

            &--wide {
                width: 88px;
            }
        }
        &__action {
            color: $color-blue-light;
            cursor: pointer;
        }
        &__title {
            font-size: 16px;
            line-height: 1.5;

            &--margin-top {
                margin-top: 8px;
            }
            
            &--margin-bottom {
                &:not(:last-child) {
                    margin-bottom: 8px;
                    &.copy-medium {
                        margin-bottom: 8px;
                    }
                }
            }
            &--padding-right {
                padding-right: 40px;
            }
            &--break {
                word-break: break-all;
            }
            &--fullwidth {
                width: 100%;
            }
            &.copy-medium {
                &:not(:last-child) {
                    margin-bottom: 2px;
                }
            }
        }
        &__texts {
            transition: 0.3s transform;
            #{$block}--hide-summary-text.el-collapse-item.is-active & {
                transform: translateY(11px);
            }

            &--expand {
                flex-grow: 1;
            }
        }
        &__text {
            transition: 0.3s opacity;
            white-space: nowrap;
            #{$block}--hide-summary-text.el-collapse-item.is-active & {
                opacity: 0;
                pointer-events: none;
            }
            &--wrap {
                white-space: initial;
            }
            p {
                margin-top: 0;
            }
        }
        &__arrow {
            transition: 0.4s $ease-out-quart;
            color: $color-grey;
            flex-shrink: 0;
            .el-collapse-item.is-active & {
                transform: rotate(180deg);
            }
            #{$block}--static &{
                transform: rotate(-90deg);
            }
            &--top {
                margin-top: 3px;
            }
        }
        &__side-icon {
            position: absolute;
            top: 0;
            right: 0;
            z-index: 2;
        }
        &__toggle {
            &--opened {
                display: none;

                .is-active & {
                    display: inline;
                }
            }
            &--closed {
                .is-active & {
                    display: none;
                }
            }
        }

        &__edit {
            position: absolute;
            right: 0;
            top: 50%;
            transform: translateY(-50%);
            line-height: 0;
        }
    }
    &__content {
        padding: 3px 0 0;

        &--space-top {
            padding-top: 11px;
        }
        &--full-width {
            padding-left: 0 !important;
        }
    }
    &__section {
        &:not(:last-child) {
            margin-bottom: 16px;
        }
        &:empty {
            margin-bottom: 0;
        }

        &--small-margin-bottom {
            &:not(:last-child) {
                margin-bottom: 8px;
            }
        }

        &--margin-top {
            margin-top: 10px;
        }

        &--border-bottom {
            position: relative;
            padding-bottom: 12px;
            &:not(:last-child) {
                margin-bottom: 12px;
            }

            &::after {
                content: '';
                position: absolute;
                bottom: 0;
                left: 0;
                right: -16px * 2;
                height: 1px;
                background-color: $color-black-005;
            }
        }
    }
    &__row {
        &--space-top {
            padding-top: 9px;
        }
        &--more-space {
            &:not(:last-child) {
                margin-bottom: 6px !important;
            }  
        }
        &--medium-space {
            &:not(:last-child) {
                margin-bottom: 2px !important;
            }  
        }
        &--black {
            color: $color-black;
        }
        &--no-space {
            margin-bottom: 0 !important;
        }
        &--link {
            cursor: pointer;
            .no-touch &:hover {
                #{$block}__row__arrow {
                    color: $color-black;
                }
            }
        }
        &:not(:last-child) {
            margin-bottom: 4px;
        }
        span {
            display: block;
        }
        .fixed-width {
            width: 50px;
        }

        &__arrow {
            transition: 0.4s $ease-out-quart;
            color: $color-grey;
            flex-shrink: 0;
        }

        &__icon {
            margin-right: 12px;
        }
    }

    &__row,
    &__summary {
        a:not(.smr-button) {
            text-decoration: none;
            color: $color-blue-light;
            border-bottom: 1px solid transparent;
            transition: border-color 0.2s $ease-out-quint;
            word-break: break-word;
            .no-touch &:hover {
                border-color: $color-blue-light;
            }
        }
        b, strong {
            font-weight: $font-weight-regular;
            color: $color-black;
        }
    }
}
</style>